// Per-page head management without pulling in react-helmet. The homepage is
// the only public marketing route, so a small imperative hook is enough.

const SITE = "https://flowstate.net.in";

const upsertMeta = (selector, attrs) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }
  Object.entries(attrs).forEach(([k, v]) => tag.setAttribute(k, v));
  return tag;
};

const upsertLink = (rel, href) => {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
  return tag;
};

const FAQS_SCHEMA = [
  {
    q: "What do I need to know before starting?",
    a: "Some coding. Not much, and not expertly — but you should be able to read a script and write a simple one without it being your first time. Ideally you are already working in IT or a data-adjacent role, because the transition is far shorter from there and you will have real problems to apply this to. If you are starting from genuinely zero, learn basic Python first and come back; we will tell you that honestly on the call rather than take your money and hope. What we do not require is a degree in mathematics, a computer science background, or any prior AI experience."
  },
  {
    q: "What does 'milestone-bound' actually mean?",
    a: "Your plan is organised around tangible milestones — master statistics and matrix calculus, train a neural network from scratch, deploy a production RAG system on AWS — rather than dates. You move to the next one when you have genuinely mastered the current one. Nothing expires, and a busy month at work costs you nothing."
  },
  {
    q: "Why do you strictly limit intake to 50 candidates at a time?",
    a: "We are practicing AI engineers, data scientists and startup founders, and we teach every session ourselves. Weekly 1:1 sessions, personalised assignments, line-by-line code review and provisioning cloud GPU infrastructure takes real time per person. Fifty is simply how many we can do that for properly."
  },
  {
    q: "Do you really provide AWS cloud compute credits and Google Colab Pro subscriptions?",
    a: "Yes. You cannot learn real AI engineering by reading slides or running toy notebooks on localhost. We provide AWS cloud compute credits and premium Google Colab subscriptions so you train models, run evaluations, optimize inference, and deploy production endpoints on actual cloud GPU hardware.",
  },
  {
    q: "Will I work with the same people throughout?",
    a: "Yes. The same small team designs your roadmap, writes your study material and assessments, reviews your code and teaches you one to one, from the first conversation to the last. Nobody gets reassigned, and you never have to explain your history again."
  },
  {
    q: "Can I focus exclusively on advanced specializations like LLM Internals, CUDA, or building my own AI product?",
    a: "Yes. Nobody is forced to take courses they don't need. If you already know software engineering and want to focus purely on LLM internals, attention mechanisms, CUDA/Triton kernel programming, or AI system design (HLD & LLD), or if you are a founder architecting an AI startup, we build your milestone plan around that exact aspiration.",
  },
];

// Organization + Course + FAQPage structured data for maximum Google rich snippet visibility.
export const buildStructuredData = () => ({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "EducationalOrganization",
      "@id": `${SITE}/#organization`,
      name: "FlowState",
      url: SITE,
      description:
        "A close-knit group of practicing AI engineers, data scientists, and founders teaching AI one to one. Milestone-bound roadmaps from mathematical foundations through machine learning, deep learning, AI engineering, LLM internals, kernel programming, and production cloud systems.",
      email: "core@edgent.in",
      telephone: "+91-8340156311",
      sameAs: ["https://www.linkedin.com/company/edgent-ai/"],
      address: {
        "@type": "PostalAddress",
        addressCountry: "IN",
      },
    },
    {
      "@type": "WebSite",
      "@id": `${SITE}/#website`,
      url: SITE,
      name: "FlowState",
      publisher: { "@id": `${SITE}/#organization` },
      inLanguage: "en-IN",
    },
    {
      "@type": "Course",
      name: "1:1 AI Engineering & Machine Learning Mentorship",
      description:
        "Personalised, milestone-based AI tutoring taught 1:1 by active engineers and founders. Covers Linear Algebra, Statistics, Classical ML, Deep Learning, Generative AI, RAG Systems, LLM Internals, CUDA Kernel Programming, and AI System Design (HLD & LLD). AWS cloud credits and Google Colab Pro included. Strictly 50 candidates.",
      url: SITE,
      provider: { "@id": `${SITE}/#organization` },
      inLanguage: "en-IN",
      educationalCredentialAwarded: "AI Engineering & Product Readiness",
      hasCourseInstance: {
        "@type": "CourseInstance",
        courseMode: "online",
        courseWorkload: "P30H",
      },
    },
    {
      "@type": "FAQPage",
      "@id": `${SITE}/#faq`,
      mainEntity: FAQS_SCHEMA.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: f.a,
        },
      })),
    },
  ],
});

export const useHomepageSeo = () => {
  const title = "FlowState — 1:1 AI Engineering Tutoring & Mentorship | Milestone-Bound";
  const description =
    "1:1 AI tutoring from practicing AI engineers & founders. Milestone-bound roadmaps from statistics & ML to LLM internals, CUDA kernels, and AI system design (HLD/LLD). AWS & Colab compute provided. Strictly 50 candidates.";
  const image = `${SITE}/social-card.png`;

  document.title = title;
  upsertMeta('meta[name="description"]', { name: "description", content: description });
  upsertLink("canonical", `${SITE}/`);

  upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
  upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
  upsertMeta('meta[property="og:description"]', {
    property: "og:description",
    content: description,
  });
  upsertMeta('meta[property="og:url"]', { property: "og:url", content: `${SITE}/` });
  upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
  upsertMeta('meta[property="og:site_name"]', {
    property: "og:site_name",
    content: "FlowState",
  });
  upsertMeta('meta[name="twitter:card"]', {
    name: "twitter:card",
    content: "summary_large_image",
  });
  upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
  upsertMeta('meta[name="twitter:description"]', {
    name: "twitter:description",
    content: description,
  });
  upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

  // JSON-LD, replaced rather than appended so it can't accumulate.
  const existing = document.getElementById("ld-homepage");
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = "ld-homepage";
  script.textContent = JSON.stringify(buildStructuredData());
  document.head.appendChild(script);
};

export default useHomepageSeo;
